/**
 * OEM Extras (OpenEye Marketing) — four small standalone widgets:
 *  <oem-sms-optin>        Postscript SMS opt-in (mobile shortlink / desktop embedded form)
 *  <oem-countdown>        drop countdown ticker (DD:HH:MM:SS)
 *  <oem-clock>            visitor's local time, sticky bottom
 *  <oem-discount-banner>  top-of-page discount banner w/ tap-to-copy code
 * No imports — safe to load anywhere, including the password layout.
 */

/* ---------- SMS opt-in (Postscript) ---------- */

class OEMSmsOptin extends HTMLElement {
  connectedCallback() {
    const link = this.querySelector('[data-oem-sms-link]');
    const btn = this.querySelector('[data-oem-sms-reveal]');
    const formWrap = this.querySelector('[data-oem-sms-form]');

    // Touch devices: the shortlink escapes in-app browsers cleanly and opens
    // the native SMS composer. Desktop: reveal the embedded Postscript form.
    const isCoarse = matchMedia('(pointer: coarse)').matches;

    if (isCoarse && link) {
      link.hidden = false;
      return;
    }

    if (btn && formWrap) {
      btn.hidden = false;
      btn.addEventListener('click', () => {
        btn.hidden = true;
        formWrap.hidden = false;
        OEMSmsOptin.loadSdk();
      });
    } else if (link) {
      // No embedded form configured — fall back to the shortlink everywhere.
      link.hidden = false;
    }
  }

  static loadSdk() {
    if (document.getElementById('oem-postscript-sdk')) return;
    const s = document.createElement('script');
    s.id = 'oem-postscript-sdk';
    s.src = 'https://sdk.postscript.io/embeddedForm.bundle.js';
    s.async = true;
    document.head.appendChild(s);
  }
}

/* ---------- Drop countdown ---------- */

class OEMCountdown extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.#tick();
    this.#timer = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  #tick() {
    const target = new Date(this.dataset.date || '').getTime();
    if (Number.isNaN(target)) return;

    const units = this.querySelectorAll('[data-oem-cd-unit]');
    const distance = target - Date.now();

    if (distance <= 0) {
      clearInterval(this.#timer);
      this.setAttribute('data-done', 'true');
      this.removeAttribute('data-urgent');
      if (this.hasAttribute('data-hide-when-done')) {
        this.hidden = true;
        return;
      }
      const doneEl = this.querySelector('[data-oem-cd-done]');
      if (this.dataset.doneText && doneEl) {
        doneEl.textContent = this.dataset.doneText;
        doneEl.hidden = false;
      }
      this.#setUnits(units, 0, 0, 0, 0);
      const single = this.querySelector('[data-oem-countdown-value]');
      if (single && !units.length) single.textContent = this.dataset.doneText || '00:00:00:00';
      return;
    }

    const days = Math.floor(distance / 86400000);
    const hours = Math.floor(distance / 3600000) % 24;
    const minutes = Math.floor(distance / 60000) % 60;
    const seconds = Math.floor(distance / 1000) % 60;

    // Final-minute urgency: drives a pulsing accent via CSS.
    if (distance < 60000) this.setAttribute('data-urgent', '');
    else this.removeAttribute('data-urgent');

    if (units.length) {
      this.#setUnits(units, days, hours, minutes, seconds);
    } else {
      const out = this.querySelector('[data-oem-countdown-value]') || this;
      const pad = (n) => String(n).padStart(2, '0');
      out.textContent = `${pad(days)}:${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
    }
  }

  #setUnits(units, days, hours, minutes, seconds) {
    const map = { days, hours, minutes, seconds };
    units.forEach((el) => {
      const value = String(map[el.dataset.oemCdUnit] ?? 0).padStart(2, '0');
      if (el.textContent === value) return;
      el.textContent = value;
      // restart the pop animation even on repeat ticks
      el.classList.remove('oem-cd__tick');
      void el.offsetWidth;
      el.classList.add('oem-cd__tick');
    });
  }
}

/* ---------- Visitor clock ---------- */

class OEMClock extends HTMLElement {
  #timer = 0;

  connectedCallback() {
    this.#tick();
    this.#timer = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  #tick() {
    const now = new Date();
    const date = now.toLocaleDateString(undefined, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
    const time = now.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    const label = this.dataset.label ? ` ${this.dataset.label}` : '';
    this.textContent = `${date} ${time}${label}`;
  }
}

/* ---------- Discount banner ---------- */

class OEMDiscountBanner extends HTMLElement {
  connectedCallback() {
    // Session-dismiss memory (skipped in the theme editor so it always shows).
    const key = this.dataset.key || 'oem-banner';
    const inEditor = window.Shopify && window.Shopify.designMode;
    try {
      if (!inEditor && sessionStorage.getItem(key) === '1') {
        this.hidden = true;
        return;
      }
    } catch (e) {
      /* private mode */
    }

    this.addEventListener('click', async (event) => {
      const close = event.target.closest('[data-oem-banner-close]');
      if (close) {
        this.hidden = true;
        try {
          sessionStorage.setItem(key, '1');
        } catch (e) {
          /* ignore */
        }
        return;
      }
      const codeBtn = event.target.closest('[data-oem-banner-code]');
      if (codeBtn) {
        const hint = codeBtn.querySelector('[data-oem-banner-hint]');
        try {
          await navigator.clipboard.writeText(codeBtn.dataset.code || '');
          if (hint) {
            const prev = hint.textContent;
            hint.textContent = 'Copied!';
            setTimeout(() => {
              hint.textContent = prev;
            }, 1600);
          }
        } catch (e) {
          /* clipboard blocked — code is visible anyway */
        }
      }
    });
  }
}

/* ---------- Raffle / giveaway entry portal (popup) ---------- */

class OEMRaffle extends HTMLElement {
  #shown = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#initStages();
    if (window.Shopify && window.Shopify.designMode) {
      this.#open();
      return;
    }
    if (this.#suppressed()) return;
    this.#scheduleTrigger();
  }

  get #storeKey() {
    return this.dataset.key || 'oem-raffle';
  }

  get #enteredKey() {
    return `oem-raffle-entered-${this.dataset.entryVariant || this.dataset.key}`;
  }

  /* ---------- entry stages (SMS gate -> enter) ---------- */

  get #limitOne() {
    return this.dataset.limitOne !== 'false';
  }

  #initStages() {
    // Already entered this drop? show the confirmed state (only when limited).
    if (this.#limitOne && this.#alreadyEntered()) {
      this.#showEntered();
      return;
    }
    // SMS not required -> unlock the entry stage immediately.
    if (this.dataset.requireSms === 'false') this.#revealEnter();
  }

  #alreadyEntered() {
    try {
      return localStorage.getItem(this.#enteredKey) === '1';
    } catch (e) {
      return false;
    }
  }

  #revealEnter() {
    const sms = this.querySelector('[data-oem-raffle-stage="sms"]');
    const enter = this.querySelector('[data-oem-raffle-stage="enter"]');
    if (sms) sms.hidden = true;
    if (enter) enter.hidden = false;
  }

  #showEntered() {
    this.querySelectorAll('[data-oem-raffle-stage]').forEach((s) => (s.hidden = true));
    const done = this.querySelector('[data-oem-raffle-entered]');
    if (done) done.hidden = false;
  }

  async #enter(button) {
    const variant = this.dataset.entryVariant;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (!variant) {
      // No entry product configured — just mark intent and close gracefully.
      window.location.href = this.dataset.enterFallback || `${root}`;
      return;
    }
    button.disabled = true;
    const label = button.textContent;
    button.textContent = 'Entering…';
    try {
      const fd = new FormData();
      fd.set('id', variant);
      fd.set('quantity', '1');
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: fd,
      });
      if (!res.ok) throw new Error('add failed');
      if (this.#limitOne) {
        try {
          localStorage.setItem(this.#enteredKey, '1');
        } catch (e) {
          /* ignore */
        }
      }
      // Entry is recorded once the paid entry checks out as an order.
      window.location.href = `${root}checkout`;
    } catch (e) {
      button.disabled = false;
      button.textContent = label;
    }
  }

  #suppressed() {
    const freq = this.dataset.frequency || 'session';
    if (freq === 'always') return false;
    try {
      if (freq === 'session') return sessionStorage.getItem(this.#storeKey) === '1';
      const last = parseInt(localStorage.getItem(this.#storeKey) || '0', 10);
      const days = parseInt(this.dataset.frequencyDays || '7', 10);
      return Date.now() - last < days * 86400000;
    } catch (e) {
      return false;
    }
  }

  #remember() {
    const freq = this.dataset.frequency || 'session';
    try {
      if (freq === 'session') sessionStorage.setItem(this.#storeKey, '1');
      else if (freq === 'days') localStorage.setItem(this.#storeKey, String(Date.now()));
    } catch (e) {
      /* ignore */
    }
  }

  #scheduleTrigger() {
    const trigger = this.dataset.trigger || 'delay';
    if (trigger === 'load') {
      this.#open();
      return;
    }
    if (trigger === 'delay') {
      setTimeout(() => this.#open(), (parseFloat(this.dataset.delay || '8') || 0) * 1000);
      return;
    }
    if (trigger === 'scroll') {
      const pct = parseInt(this.dataset.scroll || '35', 10);
      const onScroll = () => {
        const scrolled = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight;
        if (scrolled * 100 >= pct) {
          window.removeEventListener('scroll', onScroll);
          this.#open();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      return;
    }
    if (trigger === 'exit') {
      if (matchMedia('(pointer: coarse)').matches) {
        setTimeout(() => this.#open(), 18000);
        return;
      }
      const onLeave = (event) => {
        if (event.clientY <= 0) {
          document.removeEventListener('mouseout', onLeave);
          this.#open();
        }
      };
      document.addEventListener('mouseout', onLeave);
    }
  }

  #open() {
    if (this.#shown) return;
    this.#shown = true;
    this.hidden = false;
    requestAnimationFrame(() => this.setAttribute('data-open', 'true'));
    this.#remember();
    document.addEventListener('keydown', this.#onKey);
  }

  #close() {
    this.removeAttribute('data-open');
    document.removeEventListener('keydown', this.#onKey);
    setTimeout(() => {
      this.hidden = true;
    }, 300);
  }

  #onKey = (event) => {
    if (event.key === 'Escape') this.#close();
  };

  #onClick = (event) => {
    if (event.target.closest('[data-oem-raffle-close]')) {
      this.#close();
      return;
    }
    if (event.target.closest('[data-oem-raffle-continue]')) {
      this.#revealEnter();
      return;
    }
    const enterBtn = event.target.closest('[data-oem-raffle-enter]');
    if (enterBtn) this.#enter(enterBtn);
  };
}

if (!customElements.get('oem-sms-optin')) customElements.define('oem-sms-optin', OEMSmsOptin);
if (!customElements.get('oem-countdown')) customElements.define('oem-countdown', OEMCountdown);
if (!customElements.get('oem-clock')) customElements.define('oem-clock', OEMClock);
if (!customElements.get('oem-discount-banner')) customElements.define('oem-discount-banner', OEMDiscountBanner);
if (!customElements.get('oem-raffle')) customElements.define('oem-raffle', OEMRaffle);
